import {
    ResourceSearchCorpus,
    ResourceSearchDocument,
    ResourceSearchResult,
} from '../models/resource-search.model';

const MAX_EXCERPT_LENGTH = 320;

interface ParsedQuery {
    normalized: string;
    phrases: string[];
    tokens: string[];
}

interface ScoredDocument {
    document: ResourceSearchDocument;
    score: number;
    reasons: string[];
    matchedTerms: string[];
}

export function validateResourceSearchCorpus(value: unknown): ResourceSearchCorpus {
    if (!value || typeof value !== 'object') throw new Error('Search corpus must be an object');
    const corpus = value as Partial<ResourceSearchCorpus>;
    if (corpus.schemaVersion !== '1.0') throw new Error(`Unsupported search corpus schema: ${corpus.schemaVersion ?? 'missing'}`);
    if (!corpus.resourceId) throw new Error('Search corpus resourceId is required');
    if (!corpus.packageVersion) throw new Error('Search corpus packageVersion is required');
    if (!Array.isArray(corpus.documents)) throw new Error('Search corpus documents must be an array');
    if (corpus.documentCount !== corpus.documents.length) {
        throw new Error(`Search corpus documentCount ${corpus.documentCount} does not match ${corpus.documents.length} documents`);
    }
    for (const [index, document] of corpus.documents.entries()) {
        if (!document?.id || !document.searchFields || !document.sourceLocator?.pdfPage) {
            throw new Error(`Search corpus document ${index} is missing required fields`);
        }
        if (document.resourceId !== corpus.resourceId) {
            throw new Error(`Search corpus document ${document.id} belongs to a different resource`);
        }
    }
    return corpus as ResourceSearchCorpus;
}

/**
 * Deterministic, dependency-free ranking shared by Prism and Agent API.
 * Scores intentionally favor exact identifiers and headings over body occurrences.
 */
export function searchResourceCorpus(
    corpus: ResourceSearchCorpus,
    query: string,
    limit = 30,
): ResourceSearchResult[] {
    const parsed = parseQuery(query);
    if (!parsed.normalized || limit <= 0) return [];

    return corpus.documents
        .map(document => scoreDocument(document, parsed))
        .filter((value): value is ScoredDocument => value !== null)
        .sort((left, right) =>
            right.score - left.score
            || left.document.sourceLocator.pdfPage - right.document.sourceLocator.pdfPage
            || left.document.id.localeCompare(right.document.id))
        .slice(0, Math.floor(limit))
        .map(value => ({
            resourceId: value.document.resourceId,
            document: value.document,
            score: value.score,
            excerpt: createExcerpt(value.document.searchFields.body, parsed),
            matchedTerms: value.matchedTerms,
            matchReason: value.reasons[0] ?? 'Text match',
        }));
}

/** Searches multiple linked Resources as one result set. */
export function searchResourceCorpora(
    corpora: ResourceSearchCorpus[],
    query: string,
    limit = 30,
): ResourceSearchResult[] {
    return corpora
        .flatMap(corpus => searchResourceCorpus(corpus, query, Math.max(limit, 30)))
        .sort((left, right) =>
            right.score - left.score
            || left.document.sourceLocator.pdfPage - right.document.sourceLocator.pdfPage
            || left.document.id.localeCompare(right.document.id))
        .slice(0, Math.max(0, Math.floor(limit)));
}

function scoreDocument(document: ResourceSearchDocument, query: ParsedQuery): ScoredDocument | null {
    const fields = document.searchFields;
    const code = normalize(fields.code);
    const title = normalize(fields.title || document.title);
    const heading = normalize(fields.heading);
    const aliases = normalize(fields.aliases.join(' '));
    const body = normalize(fields.body);
    const sourceText = `${code} ${title} ${heading} ${body}`;
    const combined = `${sourceText} ${aliases}`;
    // Quotes mean the wording must really exist in the source. Derived aliases
    // improve ordinary searches but must not masquerade as quoted PDF text.
    if (query.phrases.some(phrase => !sourceText.includes(phrase))) return null;
    const compactQuery = query.normalized.replace(/\s/g, '');
    const compactCode = code.replace(/\s/g, '');
    let score = 0;
    const reasons: string[] = [];
    const matchedTerms = new Set<string>();

    if (compactCode && compactCode === compactQuery) {
        score += 1200;
        reasons.push('Exact code');
        matchedTerms.add(fields.code);
    } else if (!query.phrases.length && query.normalized.length >= 3) {
        if (title.includes(query.normalized)) addScore(450, 'Exact title phrase', query.normalized);
        else if (heading.includes(query.normalized)) addScore(380, 'Exact heading phrase', query.normalized);
        else if (aliases.includes(query.normalized)) addScore(240, 'Alias phrase', query.normalized);
        else if (body.includes(query.normalized)) addScore(180, 'Exact text phrase', query.normalized);
    }

    for (const phrase of query.phrases) {
        if (title.includes(phrase)) addScore(500, 'Exact title phrase', phrase);
        else if (heading.includes(phrase)) addScore(420, 'Exact heading phrase', phrase);
        else if (body.includes(phrase)) addScore(220, 'Exact text phrase', phrase);
    }

    let matchedTokenCount = 0;
    for (const token of query.tokens) {
        let matched = false;
        if (hasWholeTerm(code, token)) addScore(260, 'Code match', token);
        if (hasWholeTerm(title, token)) addScore(80, 'Title match', token);
        if (hasWholeTerm(heading, token)) addScore(65, 'Heading match', token);
        if (hasWholeTerm(aliases, token)) addScore(45, 'Alias match', token);
        if (hasWholeTerm(body, token)) addScore(12, 'Text match', token);
        matched = matchedTerms.has(token);

        if (!matched && token.length >= 3 && hasPrefix(combined, token)) {
            addScore(6, 'Prefix match', token);
            matched = true;
        }
        if (!matched && token.length >= 5 && hasBoundedTypo(combined, token)) {
            addScore(2, 'Close text match', token);
            matched = true;
        }
        if (matched) matchedTokenCount++;
    }

    // Multi-word searches should not rank pages that happen to contain only one common word.
    const requiredTokens = query.tokens.length <= 1 ? 1 : Math.ceil(query.tokens.length * 0.6);
    if (!score || matchedTokenCount < requiredTokens) return null;
    if (matchedTokenCount === query.tokens.length && query.tokens.length > 1) {
        score += 100;
        reasons.push('All terms');
    }

    return { document, score, reasons: unique(reasons), matchedTerms: unique([...matchedTerms]) };

    function addScore(points: number, reason: string, term: string): void {
        score += points;
        reasons.push(reason);
        matchedTerms.add(term);
    }
}

function parseQuery(value: string): ParsedQuery {
    const normalized = normalize(value);
    const phrases = [...value.matchAll(/"([^"]+)"/g)]
        .map(match => normalize(match[1]))
        .filter(Boolean);
    const withoutQuotes = normalize(value.replace(/"[^"]+"/g, ' '));
    const tokens = unique(withoutQuotes.split(' ').filter(token => token.length >= 2));
    if (!tokens.length && phrases.length) tokens.push(...unique(phrases.flatMap(phrase => phrase.split(' '))));
    return { normalized, phrases, tokens };
}

function normalize(value: string): string {
    return (value ?? '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('en-US')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

function hasWholeTerm(haystack: string, needle: string): boolean {
    if (!needle) return false;
    return (` ${haystack} `).includes(` ${needle} `);
}

function hasPrefix(haystack: string, token: string): boolean {
    return haystack.split(' ').some(word => word.startsWith(token));
}

function hasBoundedTypo(haystack: string, token: string): boolean {
    const maxDistance = token.length >= 8 ? 2 : 1;
    return haystack.split(' ').some(word =>
        Math.abs(word.length - token.length) <= maxDistance
        && editDistanceAtMost(word, token, maxDistance));
}

function editDistanceAtMost(left: string, right: string, maximum: number): boolean {
    if (Math.abs(left.length - right.length) > maximum) return false;
    let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
        const current = [leftIndex];
        let rowMinimum = current[0];
        for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
            const value = Math.min(
                current[rightIndex - 1] + 1,
                previous[rightIndex] + 1,
                previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
            );
            current.push(value);
            rowMinimum = Math.min(rowMinimum, value);
        }
        if (rowMinimum > maximum) return false;
        previous = current;
    }
    return previous[right.length] <= maximum;
}

function createExcerpt(body: string, query: ParsedQuery): string {
    const clean = (body ?? '').replace(/\s+/g, ' ').trim();
    if (clean.length <= MAX_EXCERPT_LENGTH) return clean;
    const lower = clean.toLocaleLowerCase('en-US');
    const candidates = [...query.phrases, ...query.tokens].filter(Boolean);
    const found = candidates
        .map(term => lower.indexOf(term.toLocaleLowerCase('en-US')))
        .filter(index => index >= 0)
        .sort((a, b) => a - b)[0] ?? 0;
    const start = Math.max(0, found - 90);
    const end = Math.min(clean.length, start + MAX_EXCERPT_LENGTH);
    return `${start > 0 ? '…' : ''}${clean.slice(start, end).trim()}${end < clean.length ? '…' : ''}`;
}

function unique<T>(values: T[]): T[] {
    return [...new Set(values)];
}
